// DeleteConfirmDialog - confirmation modal for item deletion
// Implements US-3.1: Item CRUD and Drag-Drop Reordering

import React from 'react';
import styles from './DeleteConfirmDialog.module.css';

interface DeleteConfirmDialogProps {
  isOpen: boolean;
  itemTitle: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
}

/**
 * RENDER delete confirmation dialog
 * - Modal overlay with confirmation prompt
 * - Shows item title being deleted
 * - Confirm/Cancel actions
 */
export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  isOpen,
  itemTitle,
  onConfirm,
  onCancel,
  isDeleting = false,
}) => {
  /**
   * HANDLE escape key (close modal)
   * NOTE: Must be before early return to satisfy React hooks rules
   */
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return;
  }, [isOpen, onCancel]);

  /**
   * HANDLE backdrop click (close modal)
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  // EARLY return after all hooks
  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.dialog}>
        {/* HEADER */}
        <div className={styles.header}>
          <h3>Thema / Element löschen</h3>
        </div>

        {/* CONTENT */}
        <div className={styles.content}>
          <p>Möchtest du dieses Thema / Element wirklich löschen?</p>
          <div className={styles.itemPreview}>
            <strong>{itemTitle}</strong>
          </div>
          <p className={styles.warning}>
            Diese Aktion kann nicht rückgängig gemacht werden. Alle Bewertungen dieses Themas / Elements werden ebenfalls gelöscht.
          </p>
        </div>

        {/* ACTIONS */}
        <div className={styles.actions}>
          <button
            className={styles.cancelButton}
            onClick={onCancel}
            disabled={isDeleting}
          >
            Abbrechen
          </button>
          <button
            className={styles.confirmButton}
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Lösche...' : 'Thema / Element löschen'}
          </button>
        </div>
      </div>
    </div>
  );
};
